/**
 * The typed UI store (@preact/signals). Screens and HUD components read signals; the match controller, online
 * client and audio bridge write them. Keep it free of Preact components and DOM access so it is unit-testable.
 *
 * HUD data flow: match controller → `ui.hud.value = hudSnapshot(state)` only when it changed (`hudEquals`), and
 * pushes super-titles / captions from sim events. Components never read SimState directly.
 * Screen changes go through `useActions().goto` (SceneManager), never by writing `screen` directly.
 */
import { batch, computed, signal } from '@preact/signals-core';
import type { HudSnapshot, SimEvent } from '@hf/sim';
import { getPlatform } from '../platform';
import { loadSettings, saveSettings, type KeyValueStorage } from './settings';
import type { LessonId, TutorialCommand } from './tutorial/catalog';
import { createTeachFrame } from './tutorial/teach-frame';
import type {
  Caption,
  DebugStats,
  HudPulse,
  LowerThird,
  MatchResultsView,
  MatchSetup,
  NetUiState,
  OverlayId,
  ScreenId,
  Settings,
  SuperTitle,
  SuperTitleExtras,
  SuperTitleTone,
  Toast,
  TutorialView,
  PracticeView,
  MatchIntroView,
  NetStall,
  ReplayClipInfo,
  ReplayView,
} from './types';

/** Receives each frame's de-duplicated sim events (see `ingestEvents`). */
export type SimEventListener = (events: readonly SimEvent[]) => void;

/**
 * `storage`: where settings load from and save to. Omitted = the installed platform's storage, resolved ONCE here
 * and used for both, so a store can never load defaults from one place and then overwrite the player's saved
 * settings (session token included) in another. Pass `null` for a store that never persists.
 */
export const createUiStore = (storage?: KeyValueStorage | null) => {
  const st: KeyValueStorage | null = storage === undefined ? getPlatform().storage() : storage;
  const settings = signal<Settings>(loadSettings(st));
  const screen = signal<ScreenId>('boot');
  const overlay = signal<OverlayId>('none');
  const matchSetup = signal<MatchSetup | null>(null);
  const hud = signal<HudSnapshot | null>(null);
  const superTitle = signal<SuperTitle | null>(null);
  const captions = signal<Caption[]>([]);
  const toasts = signal<Toast[]>([]);
  const net = signal<NetUiState>({ status: 'offline' });
  const results = signal<MatchResultsView | null>(null);
  const debug = signal<DebugStats | null>(null);
  const lowerThird = signal<LowerThird | null>(null);
  const pulse = signal<HudPulse | null>(null);
  const eventListeners = new Set<SimEventListener>();
  /** Tutorial / practice HUD state (written by the match scene; null outside those modes). */
  const tutorial = signal<TutorialView | null>(null);
  const practice = signal<PracticeView | null>(null);
  const paused = computed(
    () => overlay.value === 'pause' || overlay.value === 'move_list' || overlay.value === 'how_to_play',
  );
  /** Football 101: the lesson the next tutorial match starts with (set by the hub / first-launch offer). */
  const tutorialPlan = signal<{ lesson: LessonId } | null>(null);
  /** Football 101: a command for the running lesson engine (clear card, pause items); `id` changes every time. */
  const tutorialCmd = signal<{ id: number; cmd: TutorialCommand } | null>(null);
  /** Football 101 field guides in screen space (plain object, written every frame; see tutorial/teach-frame.ts). */
  const teach = createTeachFrame();
  /** The match's "TAKING THE FIELD" transition (null once the curtain is gone / outside a match). */
  const matchIntro = signal<MatchIntroView | null>(null);
  /** The instant replay on screen (null = live picture). */
  const replay = signal<ReplayView | null>(null);
  /** Highlights recorded in the current / last match, best first (Results reel). */
  const replays = signal<ReplayClipInfo[]>([]);
  /** The offense may snap now (false during the first moments after the line-up, when a press is ignored). */
  const snapReady = signal(true);
  /**
   * Online match: the rollback session is stalled waiting for the peer's inputs (null = flowing). Written by the
   * match scene from the session status ('stalled' → { since, timeoutMs }, anything else → null); the NetBanner
   * shows "WAITING FOR OPPONENT… n" counting down to the disconnect timeout instead of a silent freeze.
   */
  const netStall = signal<NetStall | null>(null);

  let nextId = 1;
  const timers = new Map<number, ReturnType<typeof setTimeout>>();
  const later = (id: number, ms: number, fn: () => void): void => {
    if (typeof setTimeout === 'undefined') return;
    timers.set(
      id,
      setTimeout(() => (timers.delete(id), fn()), ms),
    );
  };

  /** A lower-third that arrived during an instant replay (only the newest is kept: it is the current news). */
  let heldLower: (LowerThird & { durationMs: number }) | null = null;
  const putLower = ({ durationMs, ...l }: LowerThird & { durationMs: number }): void => {
    lowerThird.value = l;
    later(l.id, durationMs, () => {
      if (lowerThird.value?.id === l.id) lowerThird.value = null;
    });
  };
  replay.subscribe((r) => {
    if (r !== null || !heldLower) return;
    const held = heldLower;
    heldLower = null;
    putLower(held);
  });

  return {
    settings,
    screen,
    overlay,
    matchSetup,
    hud,
    superTitle,
    captions,
    toasts,
    net,
    results,
    debug,
    lowerThird,
    pulse,
    tutorial,
    practice,
    paused,
    matchIntro,
    replay,
    replays,
    snapReady,
    netStall,
    tutorialPlan,
    tutorialCmd,
    teach,

    /** Send a command to the running Football 101 lesson engine. */
    tutorialCommand(cmd: TutorialCommand): void {
      tutorialCmd.value = { id: nextId++, cmd };
    },

    /**
     * INTERNAL — only the SceneManager calls this (screens use `useActions().goto`, which runs scene exit/enter
     * hooks). Writing `ui.screen` from anywhere else leaks the match loop/renderer/listeners.
     */
    _setScreen(to: ScreenId): void {
      batch(() => {
        overlay.value = 'none';
        // Super-titles and lower-thirds are match broadcast graphics: they never spill onto the next screen.
        if (screen.value === 'match' && to !== 'match') {
          superTitle.value = null;
          lowerThird.value = null;
          heldLower = null;
          netStall.value = null;
        }
        screen.value = to;
      });
    },

    updateSettings(patch: Partial<Settings>): void {
      settings.value = { ...settings.value, ...patch };
      saveSettings(settings.value, st);
    },

    /**
     * Show a super-title. A 'pop' callout never replaces a 'big' title that is still on screen (returns -1).
     */
    showSuperTitle(
      text: string,
      tone: SuperTitleTone = 'gold',
      sub?: string,
      durationMs = 1600,
      extras: SuperTitleExtras = {},
    ): number {
      const current = superTitle.value;
      if (extras.size === 'pop' && current && (current.size ?? 'big') === 'big') return -1;
      const id = nextId++;
      superTitle.value = { id, text, tone, durationMs, ...(sub ? { sub } : {}), ...extras };
      later(id, durationMs, () => {
        if (superTitle.value?.id === id) superTitle.value = null;
      });
      return id;
    },

    /**
     * Broadcast lower-third (drive start, play results, penalties). While an instant replay is on screen the replay
     * banner owns the bottom-left slot: the newest lower-third waits and runs (full length) once the tape is gone.
     */
    showLowerThird(title: string, sub?: string, tone: SuperTitleTone = 'gold', durationMs = 2200): number {
      const id = nextId++;
      if (replay.peek() !== null) {
        heldLower = { id, title, tone, durationMs, ...(sub ? { sub } : {}) };
        return id;
      }
      putLower({ id, title, tone, durationMs, ...(sub ? { sub } : {}) });
      return id;
    },

    /** One-shot HUD pulse (1ST DOWN flash, stamina denied shake, power full glow, GO!). */
    pulseHud(kind: HudPulse['kind'], player = -1, text?: string): number {
      const id = nextId++;
      pulse.value = { id, kind, player, ...(text ? { text } : {}) };
      return id;
    },

    /**
     * The match controller forwards each frame's (de-duplicated) sim events here, right after
     * `driver.drainEvents`. UI moment trackers (super-titles, tutorial drills) subscribe with `onSimEvents`.
     */
    ingestEvents(events: readonly SimEvent[]): void {
      for (const l of eventListeners) l(events);
    },

    onSimEvents(listener: SimEventListener): () => void {
      eventListeners.add(listener);
      return () => void eventListeners.delete(listener);
    },

    pushCaption(text: string, durationMs = 2400): void {
      const id = nextId++;
      captions.value = [...captions.value.slice(-2), { id, text, durationMs }];
      later(id, durationMs, () => (captions.value = captions.value.filter((c) => c.id !== id)));
    },

    toast(text: string, kind: Toast['kind'] = 'info', durationMs = 3000): void {
      const id = nextId++;
      toasts.value = [...toasts.value, { id, text, kind }];
      later(id, durationMs, () => (toasts.value = toasts.value.filter((t) => t.id !== id)));
    },

    dispose(): void {
      for (const t of timers.values()) clearTimeout(t);
      timers.clear();
      eventListeners.clear();
    },
  };
};

export type UiStore = ReturnType<typeof createUiStore>;

/**
 * The app-wide store instance. Created when this module is first imported, on the platform installed at that moment
 * (its storage is bound for good): shells install their platform before importing any ui-model module.
 */
export const ui: UiStore = createUiStore();
